import { useState } from 'react';
import { api } from '../../lib/api';
import type { Message } from './types';
import { fmtDateTime } from './convUtils';
import ChannelIcon from './ChannelIcon';

export default function MessageBubble({ m }: { m: Message }) {
  const isOutbound = m.direction === 'outbound';
  const t = (m.message_type || '').toLowerCase();
  const isCall = /call|voicemail/.test(t);
  const isEmail = /email|mail/.test(t);

  // Calls/voicemails aren't conversational text — compact centered card.
  if (isCall) {
    const channel = /voicemail/.test(t) ? 'voicemail' : 'call';
    const mins = m.duration != null ? Math.floor(m.duration / 60) : null;
    const secs = m.duration != null ? m.duration % 60 : null;
    const durLabel = m.duration != null ? `${mins}:${String(secs).padStart(2, '0')}` : null;
    return (
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 text-gray-600 px-3 py-1.5 text-xs">
          <ChannelIcon channel={channel} size={13} />
          <span className="capitalize">{isOutbound ? 'Outbound' : 'Inbound'} {channel}</span>
          {durLabel && <span className="text-gray-400">· {durLabel}</span>}
          <span className="text-gray-400">· {fmtDateTime(m.date_added)}</span>
          {m.recording_url && (
            <a href={m.recording_url} target="_blank" rel="noreferrer" className="text-info hover:underline">Recording</a>
          )}
        </div>
      </div>
    );
  }

  if (isEmail) return <EmailMessage m={m} />;

  // SMS / web chat bubble.
  const failed = m.status === 'failed';
  const pending = m.status === 'pending';
  const unconfirmed = m.status === 'unconfirmed'; // I4: it may have gone out
  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm ${
          failed
            ? 'bg-red-50 text-red-800 border border-red-200 rounded-br-sm'
            : unconfirmed
            ? 'bg-warning-wash text-gray-900 border border-warning/40 rounded-br-sm'
            : isOutbound
            ? 'bg-primary-50 text-primary-900 rounded-br-sm'
            : 'bg-gray-100 text-gray-900 rounded-bl-sm'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">
          {m.body || <span className="italic text-gray-400">(no body)</span>}
        </p>
        <div className={`mt-1 text-[11px] ${failed ? 'text-red-700' : unconfirmed ? 'text-warning' : isOutbound ? 'text-primary-400' : 'text-gray-400'}`}>
          {/* CONV2 (a): the server's reason, not just the word - on 10-15 it was "This contact has no phone number for SMS". */}
          {pending || failed || unconfirmed ? sendState(m) : fmtDateTime(m.date_added)}
        </div>
      </div>
    </div>
  );
}

/** Where a send from this screen stands: on its way, refused (with the server's reason), or not confirmed (I4). */
function sendState(m: Message): string {
  if (m.status === 'pending') return 'Sending…';
  if (m.status === 'unconfirmed') return m.error ? `Not confirmed - ${m.error}` : 'Not confirmed - it may have gone out';
  return m.error ? `Failed to send - ${m.error}` : 'Failed to send';
}

// Email body lives behind a separate id; load it on demand and render in a
// sandboxed iframe (scripts disabled) so untrusted email HTML can't run.
function EmailMessage({ m }: { m: Message }) {
  const isOutbound = m.direction === 'outbound';
  // I3 (CONV2's review): an email he is sending lives only on this screen until GoHighLevel's thread has it - no email id
  // to open, so the card read "No email body available" and nothing else, even when the send had FAILED. It shows his
  // words and where the send stands, as a text does.
  // ⛔ "On this screen" is the ID, never the status: an email GoHighLevel ITSELF records as failed has its own id and
  // its body behind an email id - deciding by status took its "View email" away (the 143rd's reviewer, Minor 4).
  const failed = m.status === 'failed';
  const pending = m.status === 'pending';
  const unconfirmed = m.status === 'unconfirmed';
  const onThisScreen = m.id.startsWith('pending-');
  const sendLine = (pending || failed || unconfirmed) && (
    <div className={`mt-1 text-[11px] ${failed ? 'text-red-700' : unconfirmed ? 'text-warning' : 'text-primary-400'}`}>
      {sendState(m)}
    </div>
  );
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (body != null || !m.email_message_id) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await api.getEmailBody(m.email_message_id);
      setBody(r.email?.body || '<p style="color:#888">(empty email)</p>');
    } catch {
      setErr('Couldn’t load this email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
      <div className={`w-full max-w-[88%] rounded-2xl border px-4 py-2.5 text-sm ${failed ? 'bg-red-50 border-red-200 text-red-800' : unconfirmed ? 'bg-warning-wash border-warning/40' : isOutbound ? 'bg-primary-50 border-primary-100' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-wide text-gray-400">
          <ChannelIcon channel="email" size={12} /> Email
          <span className="ml-auto normal-case tracking-normal">{fmtDateTime(m.date_added)}</span>
        </div>
        {m.subject && <div className="font-medium text-gray-900 mb-1 break-words">{m.subject}</div>}
        {onThisScreen ? (
          <>
            <p className="whitespace-pre-wrap break-words">{m.body || <span className="italic text-gray-400">(no body)</span>}</p>
            {sendLine}
          </>
        ) : (
          <>
            {m.email_message_id ? (
              <button onClick={toggle} className="text-xs text-info hover:underline">
                {open ? 'Hide email' : 'View email'}
              </button>
            ) : (
              <span className="text-xs text-gray-400">No email body available</span>
            )}
            {sendLine}
          </>
        )}
        {open && (
          <div className="mt-2">
            {loading ? (
              <div className="text-xs text-gray-400">Loading…</div>
            ) : err ? (
              <div className="text-xs text-red-600">{err}</div>
            ) : body ? (
              <iframe title="Email" sandbox="" srcDoc={body} className="w-full h-72 border border-gray-200 rounded bg-white" />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
